import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  Building2,
  DollarSign,
  LayoutGrid,
  List,
  MapPin,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Share2,
  Table2,
  Trash2,
  TrendingUp,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useModuleVisibility } from "@/lib/moduleVisibility";
import { ADMIN_ROLES } from "@/lib/roles";
import ShareListingModal from "@/components/ShareListingModal";

import type { BusinessListing } from "@/types";
import { api, assetUrl } from "@/lib/api";
import ListingHotBadge from "@/components/ListingHotBadge";
import { useToast } from "@/components/ui/toast";
import Loader from "@/components/ui/loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  MetricTile,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";

const PAGE_SIZE = 21;

interface LookupItem { id: number; name: string }

const STATUS_LABELS: Record<number, string> = {
  1: "Draft", 2: "Under Review", 3: "Active",
  4: "Under Offer", 5: "Sold", 6: "Expired", 7: "Withdrawn", 8: "Rejected",
  9: "LOI", 10: "Due Diligence", 11: "Escrow",
};
// Semantic status hues kept, but translucent (12% alpha backgrounds) so the
// badges sit naturally on both the Light·Gold and Dark·Gold card surfaces —
// solid pastel -100 backgrounds go unreadable on dark. Neutral statuses use
// theme tokens.
const STATUS_COLORS: Record<number, string> = {
  1: "bg-muted text-muted-foreground", 2: "bg-amber-500/12 text-amber-600",
  3: "bg-emerald-500/12 text-emerald-600", 4: "bg-cyan-500/12 text-cyan-600",
  5: "bg-sky-500/12 text-sky-600", 6: "bg-muted text-muted-foreground",
  7: "bg-orange-500/12 text-orange-600", 8: "bg-rose-500/12 text-rose-600",
  9: "bg-purple-500/12 text-purple-600", 10: "bg-indigo-500/12 text-indigo-600",
  11: "bg-teal-500/12 text-teal-600",
};

const fmt = (v: number | null | undefined): string => {
  if (!v) return "—";
  if (v >= 1_000_000_000) return `$${+(v / 1_000_000_000).toFixed(1)}B`;
  if (v >= 1_000_000)     return `$${+(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)         return `$${+(v / 1_000).toFixed(1)}K`;
  return `$${v}`;
};

const SEL = "h-11 rounded-2xl border border-border/80 bg-card/85 px-4 py-2 text-sm shadow-[0_1px_0_hsl(0_0%_100%/0.84)_inset] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25 disabled:opacity-50";

type ViewMode = "list" | "grid" | "table";

// TKT-00125: the list/grid/table choice was component state only, so it snapped
// back to "list" every time the advisor left the page. Remembered per browser —
// it's a personal working preference, not something to put in a shareable URL.
const VIEW_MODE_KEY = "bl_my_listings_view_mode";
const VIEW_MODES: ViewMode[] = ["list", "grid", "table"];

const readStoredViewMode = (): ViewMode => {
  try {
    const stored = localStorage.getItem(VIEW_MODE_KEY);
    // Validated, not cast: a hand-edited or stale value would otherwise render
    // no view at all.
    return VIEW_MODES.includes(stored as ViewMode) ? (stored as ViewMode) : "list";
  } catch {
    return "list"; // private mode / storage blocked
  }
};

// TKT-00128: the table view's Title column was a fixed 200px, which cut most
// business names off mid-word ("2006 est. Elevator Installat…") and forced
// advisors to open a listing just to read what it is. The divider to the right
// of Title can now be dragged, and the width is remembered per browser.
const TITLE_COL_KEY = "bl_my_listings_title_col_px";
const TITLE_COL_DEFAULT = 200;
const TITLE_COL_MIN = 140;
const TITLE_COL_MAX = 640;
const TITLE_COL_STEP = 24; // arrow-key nudge, for resizing without a mouse

// TKT-00126: which table headings sort, and the key each sends as `order_by`
// (suffixed _asc/_desc). Anything absent here — the actions column — stays a
// plain label. Keys must match _LISTING_SORTS in app/services/business_listing.py.
const SORT_KEY_BY_HEADING: Record<string, string> = {
  "Title": "title",
  "Status": "status",
  "Category": "category",
  "Asking Price": "price",
  "Revenue": "revenue",
  "EBITDA": "ebitda",
  "Location": "location",
};

const clampTitleWidth = (px: number) =>
  Math.min(TITLE_COL_MAX, Math.max(TITLE_COL_MIN, Math.round(px)));

export default function MyListingsPage() {
  const [listings, setListings] = useState<BusinessListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewModeState] = useState<ViewMode>(readStoredViewMode);

  // TKT-00125: persist on the way through, so the choice survives leaving the
  // page. Wrapping the setter keeps the write next to the state change rather
  // than in an effect that also fires on mount.
  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch { /* ignore — the choice still holds for this session */ }
  }, []);

  // TKT-00128: user-resizable Title column. `titleWidthRef` mirrors the live
  // value so the pointerup handler can persist the final width without the
  // listeners having to be re-bound on every pixel of the drag.
  const [titleWidth, setTitleWidth] = useState<number>(() => {
    try {
      const raw = Number(localStorage.getItem(TITLE_COL_KEY));
      return Number.isFinite(raw) && raw > 0 ? clampTitleWidth(raw) : TITLE_COL_DEFAULT;
    } catch {
      return TITLE_COL_DEFAULT; // private mode / storage blocked
    }
  });
  const titleWidthRef = useRef(titleWidth);

  const persistTitleWidth = useCallback((px: number) => {
    titleWidthRef.current = px;
    try {
      localStorage.setItem(TITLE_COL_KEY, String(px));
    } catch { /* ignore — the width still applies for this session */ }
  }, []);

  const applyTitleWidth = useCallback((px: number) => {
    const next = clampTitleWidth(px);
    titleWidthRef.current = next;
    setTitleWidth(next);
  }, []);

  const startTitleResize = useCallback((e: ReactPointerEvent<HTMLSpanElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = titleWidthRef.current;
    const onMove = (ev: PointerEvent) => applyTitleWidth(startWidth + (ev.clientX - startX));
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      // Restore the page's own cursor/selection, suppressed for the drag so the
      // pointer doesn't flicker and the row text doesn't highlight.
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      persistTitleWidth(titleWidthRef.current);
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [applyTitleWidth, persistTitleWidth]);

  const onTitleResizeKey = useCallback((e: ReactKeyboardEvent<HTMLSpanElement>) => {
    const delta = e.key === "ArrowRight" ? TITLE_COL_STEP : e.key === "ArrowLeft" ? -TITLE_COL_STEP : 0;
    if (!delta) return;
    e.preventDefault();
    const next = clampTitleWidth(titleWidthRef.current + delta);
    setTitleWidth(next);
    persistTitleWidth(next);
  }, [persistTitleWidth]);
  // Page lives in the URL (?page=N, 1-based) so returning from a listing detail
  // via "Back to listings" restores the exact page the user was on.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const setPage = useCallback(
    (next: number) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next > 1) p.set("page", String(next));
          else p.delete("page");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<number, number>>({});
  // Non-null when the last load failed — rendered in place of the empty state so
  // a broken request can never masquerade as "no listings".
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManage = hasRole("bl_advisor", ...ADMIN_ROLES);
  // Staff only: sellers have no column on the switchboard (every module reads
  // as off for them), and their New Listing button must not vanish.
  const isModuleVisible = useModuleVisibility();
  const showNewListing = !canManage || isModuleVisible("new_listing");
  // Guest sharing is an advisor/admin tool and only applies to live listings
  // (status 3) — the backend enforces both; this only decides what to render.
  const [shareTarget, setShareTarget] = useState<{ id: string; title: string } | null>(null);

  // TKT-00127: the filters live in the URL alongside ?page, so the link an
  // advisor comes back to restores the exact view they left — Florida, page 3 —
  // not an unfiltered page 1. Seeded here, mirrored back by the effect below.
  const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
  const [categoryId, setCategoryId] = useState(() => searchParams.get("category") ?? "");
  const [countryId, setCountryId] = useState(() => searchParams.get("country") ?? "");
  // Id of the default (United States) row once the lookup lands — "" if that row
  // is missing, which simply leaves the filter on "All countries".
  const defaultCountryIdRef = useRef("");
  const [countryReady, setCountryReady] = useState(false);
  const [stateId, setStateId] = useState(() => searchParams.get("state") ?? "");
  const [cityId, setCityId] = useState(() => searchParams.get("city") ?? "");
  // TKT-00126: click a column heading to sort. One string ("price_desc"), empty
  // for the server default of newest-first. Treated as a filter throughout — it
  // rides in the URL, resets to page 1 when it changes, and is part of the
  // request signature — so no separate plumbing is needed.
  const [sort, setSort] = useState(() => searchParams.get("sort") ?? "");

  const [categories, setCategories] = useState<LookupItem[]>([]);
  const [countries, setCountries] = useState<LookupItem[]>([]);
  const [allStates, setAllStates] = useState<LookupItem[]>([]);
  const [states, setStates] = useState<LookupItem[]>([]);
  const [cities, setCities] = useState<LookupItem[]>([]);

  useEffect(() => {
    // Retired categories included: their listings still need filtering.
    api.get<LookupItem[]>("/lookups/categories?include_inactive=true").then(setCategories).catch(() => {});
    api.get<LookupItem[]>("/lookups/states").then(setAllStates).catch(() => {});
    // TKT-00118: default the country to United States. The state and city
    // selects are gated on a country and nothing here persists, so advisors —
    // who list US businesses only — had to re-pick it on every visit. It is set
    // with the lookup rather than in a later effect so it lands in the first
    // request instead of arriving as a filter change, which resets to page 1.
    api.get<LookupItem[]>("/lookups/countries")
      .then((rows) => {
        setCountries(rows);
        const us = rows.find((c) => c.name === "United States");
        if (!us) return;
        defaultCountryIdRef.current = String(us.id);
        // Functional: a country restored from the URL (TKT-00127) has already
        // been applied and must win over the default.
        setCountryId((cur) => cur || String(us.id));
      })
      .catch(() => {})
      // Release the first fetch either way — a failed lookup must not leave the
      // page stuck on its loading state.
      .finally(() => setCountryReady(true));
  }, []);

  // TKT-00127: these two effects also run on mount, where an unconditional reset
  // would wipe the state/city just restored from the URL. Track the previous
  // parent value and clear only on a real change; `null` means "first run".
  const prevCountryRef = useRef<string | null>(null);
  useEffect(() => {
    if (countryId) api.get<LookupItem[]>(`/lookups/states?country_id=${countryId}`).then(setStates).catch(() => {});
    else setStates([]);
    if (prevCountryRef.current !== null && prevCountryRef.current !== countryId) {
      setStateId(""); setCityId(""); setCities([]);
    }
    prevCountryRef.current = countryId;
  }, [countryId]);

  const prevStateRef = useRef<string | null>(null);
  useEffect(() => {
    if (stateId) api.get<LookupItem[]>(`/lookups/cities?state_id=${stateId}`).then(setCities).catch(() => {});
    else setCities([]);
    if (prevStateRef.current !== null && prevStateRef.current !== stateId) setCityId("");
    prevStateRef.current = stateId;
  }, [stateId]);

  // Pure data-loader for a given page. It must NOT call setPage: setPage writes
  // the URL, which changes react-router's searchParams (and therefore the
  // identity of setSearchParams → setPage). If setPage were a dependency here,
  // fetchListings would get a new identity on every page navigation, re-firing
  // the mount/filter effect below and snapping the user back to page 1. Callers
  // own page state and pass the target page in explicitly.
  type ListingsPayload = { total_elements: number; status_counts: Record<number, number>; data: BusinessListing[] };

  const requestPage = useCallback(async (targetPage: number): Promise<ListingsPayload> => {
    const params = new URLSearchParams();
    params.set("page", String(targetPage));
    params.set("page_size", String(PAGE_SIZE));
    if (search) params.set("search", search);
    if (categoryId) params.set("industry_category_id", categoryId);
    if (countryId) params.set("country_id", countryId);
    if (stateId) params.set("state_id", stateId);
    if (cityId) params.set("city_id", cityId);
    if (sort) params.set("order_by", sort);
    return api.get<ListingsPayload>(`/business-listings/mine?${params.toString()}`);
  }, [search, categoryId, countryId, stateId, cityId, sort]);

  // setPage writes the URL, so depending on it here would change fetchListings's
  // identity on every page navigation and re-fire the mount/filter effect (see
  // the comment above it). A ref gives access to the latest setPage without
  // making it a dependency.
  const setPageRef = useRef(setPage);
  setPageRef.current = setPage;

  // TKT-00127: write the filters into the URL so the address bar always
  // describes the view on screen, and `backTo` below can hand that whole view
  // to a listing detail / edit page as the way back.
  //
  // This deletes ?page in the SAME call, rather than leaving that to setPage.
  // react-router builds the functional argument from the render's closure
  // (`nextInit(new URLSearchParams(searchParams))`), so two setSearchParams
  // calls in one commit both receive the same stale params and the second
  // silently drops the first's write. A filter change resets to page 1, so both
  // writes would land together — hence one writer, one call.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;
  const syncFiltersToUrl = useCallback(() => {
    setSearchParamsRef.current(
      (prev) => {
        const p = new URLSearchParams(prev);
        const put = (key: string, value: string) => (value ? p.set(key, value) : p.delete(key));
        put("q", search);
        put("category", categoryId);
        put("country", countryId);
        put("state", stateId);
        put("city", cityId);
        put("sort", sort);
        p.delete("page"); // a filter or sort change always returns to page 1
        return p;
      },
      { replace: true }, // filtering shouldn't stack history entries
    );
  }, [search, categoryId, countryId, stateId, cityId, sort]);

  const fetchListings = useCallback(async (targetPage = 1) => {
    setLoading(true);
    setError(null);
    try {
      let data = await requestPage(targetPage);
      // A stale ?page=N — a bookmark, or the back-link from a listing detail
      // after the set shrank — points past the end: the API answers with an
      // empty page and a non-zero total, which used to render as "you do not
      // have listings yet" until the user changed a filter (which resets to
      // page 1). Clamp to the last real page instead.
      const lastPage = Math.max(1, Math.ceil(data.total_elements / PAGE_SIZE));
      if (data.data.length === 0 && data.total_elements > 0 && targetPage > lastPage) {
        setPageRef.current(lastPage);
        data = await requestPage(lastPage);
      }
      setListings(data.data);
      setTotal(data.total_elements);
      setStatusCounts(data.status_counts ?? {});
    } catch (err: unknown) {
      // Never swallow this. A failed request rendered as an empty state is
      // indistinguishable from "no listings", which is how an advisor ended up
      // reporting missing listings with no error to go on.
      setError(err instanceof Error ? err.message : "Could not load your listings.");
      setListings([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [requestPage]);

  // Navigate to a page: update the URL (so the active page + back-link restore
  // work) and load that page. fetchListings's identity is stable across this,
  // so the mount/filter effect does not re-fire.
  const goToPage = useCallback(
    (next: number) => {
      setPage(next);
      fetchListings(next);
    },
    [setPage, fetchListings],
  );

  // Fetch on mount and whenever a filter changes. fetchListings's identity changes
  // *only* when a filter changes (it no longer closes over setPage), so we compare
  // a filter "signature" to tell a real filter change from an effect re-run:
  //   • first run           → restore the page from the URL (the ?page=N back-link)
  //   • signature changed   → a filter changed, so jump back to page 1
  //   • signature unchanged → a StrictMode/double re-run; do nothing, so the
  //                           restored page is kept instead of snapping to page 1
  // (A plain "ran once" ref can't do this: StrictMode replays the mount effect with
  // the ref already set, which would wrongly reset a deep-linked page back to 1.)
  // Page navigation goes through goToPage, which never changes this signature.
  const filterSig = `${search}|${categoryId}|${countryId}|${stateId}|${cityId}|${sort}`;
  const prevFilterSig = useRef<string | null>(null);
  useEffect(() => {
    if (!countryReady) return; // wait for the default country (see the lookup above)
    if (prevFilterSig.current === null) {
      prevFilterSig.current = filterSig;
      fetchListings(page);
    } else if (prevFilterSig.current !== filterSig) {
      prevFilterSig.current = filterSig;
      // Writes the filters and drops ?page in one call (see syncFiltersToUrl),
      // replacing the bare setPage(1) that used to live here.
      syncFiltersToUrl();
      fetchListings(1);
    }
  }, [fetchListings, countryReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Passed as router state on each listing link so ListingDetail's "Back to
  // listings" returns to this exact page. Derived from the current pathname
  // because this component serves several role prefixes (/seller/listings,
  // /bl-advisor/listings, /admin/...).
  // TKT-00127: the query string rides along now, not just ?page — it is the
  // filters as well, so coming back restores the filtered view rather than
  // dropping the advisor on an unfiltered page 1.
  const { pathname, search: queryString } = useLocation();
  const backTo = `${pathname}${queryString}`;

  // Clearing returns to the page baseline (United States), not to "All
  // countries"; that baseline doesn't count as an active filter. Advisors who do
  // want every country can still pick "All countries" in the dropdown.
  const clearFilters = () => {
    setSearch(""); setCategoryId(""); setCountryId(defaultCountryIdRef.current);
    setStateId(""); setCityId("");
  };

  const hasActiveFilters = Boolean(
    search || categoryId || stateId || cityId || (countryId && countryId !== defaultCountryIdRef.current),
  );

  // TKT-00126: cycle a heading asc → desc → default. The third click matters:
  // without it there is no way back to newest-first short of reloading the page.
  const toggleSort = (key: string) => {
    setSort((cur) => (cur === `${key}_asc` ? `${key}_desc` : cur === `${key}_desc` ? "" : `${key}_asc`));
  };

  const handleEdit = (e: React.MouseEvent, id: string) => {
    e.preventDefault(); e.stopPropagation();
    // TKT-00127: this was the one row action that carried no return address, so
    // saving an edit dropped the advisor on the role home instead of the list.
    navigate(`/listings/${id}/edit`, { state: { from: backTo } });
  };

  // Rows are wrapped in a <Link>, so the share click must be stopped from
  // navigating to the listing before the dialog opens.
  const handleShare = (e: React.MouseEvent, id: string, title: string) => {
    e.preventDefault(); e.stopPropagation();
    setShareTarget({ id, title });
  };

  const handleDelete = async (e: React.MouseEvent, id: string, title: string) => {
    e.preventDefault(); e.stopPropagation();
    if (!confirm(`Delete "${title}"? You can restore it later from Deleted Listings.`)) return;
    try {
      await api.delete(`/business-listings/${id}`);
      setListings((prev) => prev.filter((l) => l.id !== id));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    }
  };

  const catName       = (id: number | null) => categories.find((c) => c.id === id)?.name ?? null;
  const stateNameById = (id: number | null) => allStates.find((s) => s.id === id)?.name ?? states.find((s) => s.id === id)?.name ?? null;
  const countryNameById = (id: number | null) => countries.find((c) => c.id === id)?.name ?? null;
  const locationStr   = (l: BusinessListing) =>
    [l.city_name, l.state_name || stateNameById(l.state_id), l.country_name || countryNameById(l.country_id)]
      .filter(Boolean).join(", ");

  const metrics = useMemo(() => ({
    total,
    active:      statusCounts[3] ?? 0,
    underReview: statusCounts[2] ?? 0,
    draft:       statusCounts[1] ?? 0,
  }), [total, statusCounts]);

  const ViewToggle = () => (
    <div className="flex items-center gap-1 rounded-xl border border-border/60 bg-white/80 p-1">
      {([
        { mode: "list"  as ViewMode, icon: List },
        { mode: "grid"  as ViewMode, icon: LayoutGrid },
        { mode: "table" as ViewMode, icon: Table2 },
      ] as const).map(({ mode, icon: Icon }) => (
        <button
          key={mode}
          type="button"
          onClick={() => setViewMode(mode)}
          className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${
            viewMode === mode ? "bg-gold-gradient text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted"
          }`}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );

  return (
    <PageShell>
      <PageHero
        eyebrow="My Listings"
        title="Manage your inventory with a cleaner, more operator-friendly workspace."
        description="Use grid, list, or table view to manage your listings. Filter by category, location, or keyword."
        actions={
          <>
            {showNewListing && (
              <Link to="/listings/new">
                <Button size="lg" className="rounded-full">
                  <Plus className="h-4 w-4" />
                  New listing
                </Button>
              </Link>
            )}
            <Button size="lg" variant="outline" className="rounded-full border-primary/20 bg-white/80 text-foreground hover:bg-white" onClick={() => fetchListings(page)}>
              Refresh list
            </Button>
          </>
        }
        aside={
          <>
            <div className="text-[0.72rem] font-bold uppercase tracking-[0.22em] text-primary/70">Inventory summary</div>
            <div className="mt-3 text-3xl font-black tracking-tight text-foreground">{metrics.total} listings</div>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              {metrics.active} active, {metrics.underReview} under review, and {metrics.draft} drafts in your current view.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-[1.3rem] border border-border/60 bg-white/70 p-4">
                <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Active</div>
                <div className="mt-2 text-2xl font-black tracking-tight text-foreground">{metrics.active}</div>
              </div>
              <div className="rounded-[1.3rem] border border-border/60 bg-white/70 p-4">
                <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Draft</div>
                <div className="mt-2 text-2xl font-black tracking-tight text-foreground">{metrics.draft}</div>
              </div>
            </div>
          </>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile label="Total Listings" value={metrics.total} icon={Building2} tone="primary" />
        <MetricTile label="Active" value={metrics.active} icon={DollarSign} tone="emerald" />
        <MetricTile label="Under Review" value={metrics.underReview} icon={Search} tone="amber" />
        <MetricTile label="Draft" value={metrics.draft} icon={Plus} tone="slate" />
      </section>

      <PagePanel>
        <PanelHeading title="Filter your listings" description="Use category and location filters to focus on exactly the records you need to update." />
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); fetchListings(1); }} className="grid gap-3 md:grid-cols-12 md:items-end">
          <div className="relative md:col-span-3">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/55" />
            <Input placeholder="Keyword..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
          </div>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={`${SEL} md:col-span-2`}>
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={countryId} onChange={(e) => setCountryId(e.target.value)} className={`${SEL} md:col-span-2`}>
            <option value="">All countries</option>
            {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={stateId} onChange={(e) => setStateId(e.target.value)} disabled={!countryId} className={`${SEL} md:col-span-2`}>
            <option value="">All states</option>
            {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={cityId} onChange={(e) => setCityId(e.target.value)} disabled={!stateId} className={`${SEL} md:col-span-2`}>
            <option value="">All cities</option>
            {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <Button type="submit" className="rounded-full md:col-span-1">Search</Button>
        </form>
        {hasActiveFilters && (
          <div className="mt-3">
            <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-destructive hover:bg-destructive/15">
              <X className="h-3.5 w-3.5" />Clear filters
            </button>
          </div>
        )}
      </PagePanel>

      <PagePanel>
        <div className="flex items-center justify-between mb-5">
          <PanelHeading
            title="Listing cards"
            description="Open any listing to review detail or delete inventory directly from this view."
          />
          <ViewToggle />
        </div>

        {loading ? (
          <Loader className="min-h-[26vh]" />
        ) : error ? (
          <div className="rounded-[1.55rem] border border-destructive/30 bg-destructive/10 p-6 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-destructive" />
            <p className="mt-3 font-bold text-foreground">Couldn't load your listings</p>
            <p className="mt-1 text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" className="mt-4 rounded-xl" onClick={() => fetchListings(page)}>
              <RefreshCw className="h-4 w-4" />
              Try again
            </Button>
          </div>
        ) : listings.length === 0 ? (
          <EmptyPanel
            icon={Building2}
            title={hasActiveFilters ? "No listings match these filters" : "You do not have listings yet"}
            description={hasActiveFilters ? "Try broadening your search criteria." : "Create your first listing to start receiving buyer interest."}
          />
        ) : viewMode === "list" ? (
          /* ── LIST VIEW ── */
          <div className="space-y-3">
            {listings.map((l) => {
              return (
              <Link key={l.id} to={`/listings/${l.id}`} state={{ from: backTo }}>
                <article className="rounded-[1.55rem] border border-border/60 bg-white/72 p-4 shadow-[0_1px_0_hsl(0_0%_100%/0.82)_inset] transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:bg-white">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate font-display text-xl font-bold leading-snug text-foreground">{l.title}</h3>
                        <ListingHotBadge published_at={l.published_at} size="sm" />
                      </div>
                      {l.reference_code && <p className="font-mono text-[0.65rem] tracking-wide text-muted-foreground">{l.reference_code}</p>}
                      {l.overview && <p className="mt-2 line-clamp-2 text-sm leading-7 text-muted-foreground">{l.overview}</p>}
                      <div className="mt-3 flex flex-wrap gap-2 text-xs">
                        <span className={`rounded-full px-3 py-1.5 font-bold uppercase tracking-[0.14em] ${STATUS_COLORS[l.listing_status_id] || "bg-slate-100 text-slate-700"}`}>
                          {STATUS_LABELS[l.listing_status_id] || `Status ${l.listing_status_id}`}
                        </span>
                        {l.industry_category_id && (
                          <span className="rounded-full border border-primary/15 bg-primary/10 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-primary">
                            {catName(l.industry_category_id) || `Category ${l.industry_category_id}`}
                          </span>
                        )}
                        {locationStr(l) && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-slate-700">
                            <MapPin className="h-3.5 w-3.5" />
                            {locationStr(l)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        {l.asking_price && <div className="text-sm font-extrabold text-primary">{fmt(Number(l.asking_price))}</div>}
                        {l.revenue && <div className="text-xs text-muted-foreground">{fmt(Number(l.revenue))} rev</div>}
                      </div>
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleEdit(e, l.id)}
                          className={l.listing_status_id !== 3 ? "text-emerald-600 hover:bg-emerald-500/10" : "text-primary hover:bg-primary/10"}
                          title={l.listing_status_id !== 3 ? "Republish listing" : "Update listing"}
                        >
                          {l.listing_status_id !== 3 ? <RefreshCw className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                          <span className="ml-1 text-xs font-bold uppercase tracking-wide">
                            {l.listing_status_id !== 3 ? "Republish" : "Update"}
                          </span>
                        </Button>
                      )}
                      {canManage && l.listing_status_id === 3 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleShare(e, l.id, l.title)}
                          title="Share with someone outside BusinessLocating"
                          className="text-primary hover:bg-primary/10"
                        >
                          <Share2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={(e) => handleDelete(e, l.id, l.title)} className="text-destructive hover:bg-destructive/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-foreground text-white">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
              );
            })}
          </div>
        ) : viewMode === "grid" ? (
          /* ── GRID VIEW ── */
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {listings.map((l) => {
              const imgSrc = l.listing_image_url ? assetUrl(l.listing_image_url) : null;
              const cf = l.asking_price && l.ebitda && Number(l.ebitda) !== 0
                ? (Number(l.asking_price) / Number(l.ebitda)).toFixed(2) + "X"
                : null;
              return (
                <Link key={l.id} to={`/listings/${l.id}`} state={{ from: backTo }}>
                  <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-md">

                    {/* Cover image */}
                    <div className="relative h-44 w-full shrink-0 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                      {imgSrc ? (
                        <img src={imgSrc} alt={l.title} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <Building2 className="h-12 w-12 text-slate-300" />
                        </div>
                      )}
                      {/* Status badge */}
                      <div className="absolute left-3 top-3">
                        <span className={`rounded-full px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-wide shadow-sm ${STATUS_COLORS[l.listing_status_id] || "bg-slate-100 text-slate-700"}`}>
                          {STATUS_LABELS[l.listing_status_id] || "Unknown"}
                        </span>
                      </div>
                      {/* Hot badge */}
                      <div className="absolute left-3 bottom-3">
                        <ListingHotBadge published_at={l.published_at} size="md" />
                      </div>
                      {/* Action buttons */}
                      <div className="absolute right-3 top-3 flex items-center gap-1.5">
                        {canManage && (
                          <button
                            onClick={(e) => handleEdit(e, l.id)}
                            title={l.listing_status_id !== 3 ? "Republish" : "Update"}
                            className={`inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm transition-colors ${
                              l.listing_status_id !== 3
                                ? "text-emerald-600 hover:bg-emerald-500/10"
                                : "text-primary hover:bg-primary/10"
                            }`}
                          >
                            {l.listing_status_id !== 3 ? <RefreshCw className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                          </button>
                        )}
                        {canManage && l.listing_status_id === 3 && (
                          <button
                            onClick={(e) => handleShare(e, l.id, l.title)}
                            title="Share with someone outside BusinessLocating"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-primary backdrop-blur-sm transition-colors hover:bg-primary/10"
                          >
                            <Share2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={(e) => handleDelete(e, l.id, l.title)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-gray-400 backdrop-blur-sm hover:bg-red-50 hover:text-red-500 transition-colors">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="flex flex-1 flex-col gap-3 p-4">
                      <div>
                        <div className="min-h-[4.15rem]">
                          <h3 className="line-clamp-2 font-display text-xl font-bold leading-snug text-foreground">{l.title}</h3>
                        </div>
                        {l.reference_code && <p className="mt-0.5 font-mono text-[0.62rem] tracking-wide text-gray-400">{l.reference_code}</p>}
                        {locationStr(l) && (
                          <div className="mt-1 flex items-center gap-1 text-[0.65rem] text-gray-400">
                            <MapPin className="h-3 w-3" />
                            {locationStr(l)}
                          </div>
                        )}
                      </div>

                      {/* Metrics */}
                      <div className="grid grid-cols-2 gap-1.5">
                        {l.asking_price && (
                          <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                            <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">Asking</span>
                            <span className="text-xs font-extrabold text-primary">{fmt(Number(l.asking_price))}</span>
                          </div>
                        )}
                        {l.revenue && (
                          <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                            <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">Revenue</span>
                            <span className="text-xs font-extrabold text-foreground">{fmt(Number(l.revenue))}</span>
                          </div>
                        )}
                        {l.ebitda && (
                          <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                            <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">EBITDA</span>
                            <span className="text-xs font-extrabold text-foreground">{fmt(Number(l.ebitda))}</span>
                          </div>
                        )}
                        {cf && (
                          <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                            <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">C.F. Mult.</span>
                            <span className="text-xs font-extrabold text-foreground">{cf}</span>
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="mt-auto flex items-center justify-between">
                        {l.industry_category_id ? (
                          <span className="rounded-full border border-primary/15 bg-primary/10 px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-wide text-primary">
                            {catName(l.industry_category_id) || `Cat ${l.industry_category_id}`}
                          </span>
                        ) : <span />}
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-foreground text-background transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                          <ArrowRight className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        ) : (
          /* ── TABLE VIEW ── */
          <div className="overflow-x-auto rounded-2xl border border-border/60">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40">
                  {["Title", "Status", "Category", "Asking Price", "Revenue", "EBITDA", "Location", ""].map((h) => (
                    <th
                      key={h}
                      aria-sort={
                        SORT_KEY_BY_HEADING[h] && sort === `${SORT_KEY_BY_HEADING[h]}_asc`
                          ? "ascending"
                          : SORT_KEY_BY_HEADING[h] && sort === `${SORT_KEY_BY_HEADING[h]}_desc`
                          ? "descending"
                          : undefined
                      }
                      style={h === "Title" ? { width: titleWidth } : undefined}
                      className={`whitespace-nowrap px-4 py-3 text-left text-[0.65rem] font-bold uppercase tracking-[0.16em] text-muted-foreground${h === "Title" ? " relative" : ""}`}
                    >
                      {/* TKT-00126: sortable headings are buttons; the rest stay
                          plain text. aria-sort tells a screen reader which column
                          the table is ordered by. */}
                      {SORT_KEY_BY_HEADING[h] ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(SORT_KEY_BY_HEADING[h])}
                          aria-label={`Sort by ${h}`}
                          className={`group/sort inline-flex items-center gap-1 uppercase tracking-[0.16em] transition-colors hover:text-primary ${sort.startsWith(`${SORT_KEY_BY_HEADING[h]}_`) ? "text-primary" : ""}`}
                        >
                          {h}
                          {sort === `${SORT_KEY_BY_HEADING[h]}_asc` ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : sort === `${SORT_KEY_BY_HEADING[h]}_desc` ? (
                            <ArrowDown className="h-3 w-3" />
                          ) : (
                            // Only on hover: a permanent icon on every heading
                            // reads as noise across seven columns.
                            <ArrowUpDown className="h-3 w-3 opacity-0 transition-opacity group-hover/sort:opacity-40" />
                          )}
                        </button>
                      ) : (
                        h
                      )}
                      {/* TKT-00128: drag this divider to widen the Title column.
                          A 9px grab strip straddling the cell edge (the visible
                          line is the 1px core) — a hairline is too small a target
                          to hit reliably. */}
                      {h === "Title" && (
                        <span
                          role="separator"
                          aria-orientation="vertical"
                          aria-label="Drag to resize the title column"
                          aria-valuenow={titleWidth}
                          aria-valuemin={TITLE_COL_MIN}
                          aria-valuemax={TITLE_COL_MAX}
                          tabIndex={0}
                          onPointerDown={startTitleResize}
                          onKeyDown={onTitleResizeKey}
                          onDoubleClick={() => { setTitleWidth(TITLE_COL_DEFAULT); persistTitleWidth(TITLE_COL_DEFAULT); }}
                          title="Drag to resize · double-click to reset"
                          className="group absolute -right-1 top-0 z-10 flex h-full w-[9px] cursor-col-resize touch-none items-stretch justify-center focus:outline-none"
                        >
                          <span className="w-px bg-border transition-colors group-hover:w-0.5 group-hover:bg-primary group-focus:w-0.5 group-focus:bg-primary" />
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {listings.map((l) => (
                  <tr key={l.id} className="group bg-white transition-colors hover:bg-primary/[0.03]">
                    <td style={{ width: titleWidth, maxWidth: titleWidth }} className="px-4 py-3 font-semibold text-foreground">
                      <div className="flex items-center gap-1.5">
                        <Link to={`/listings/${l.id}`} state={{ from: backTo }} className="truncate hover:text-primary">{l.title}</Link>
                        <ListingHotBadge published_at={l.published_at} size="sm" />
                        {l.reference_code && <span className="shrink-0 font-mono text-[0.65rem] font-normal text-muted-foreground">{l.reference_code}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-wide ${STATUS_COLORS[l.listing_status_id] || "bg-slate-100 text-slate-700"}`}>
                        {STATUS_LABELS[l.listing_status_id] || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {catName(l.industry_category_id) || "—"}
                    </td>
                    <td className="px-4 py-3 font-extrabold text-primary">
                      <span className="inline-flex items-center gap-1">
                        <TrendingUp className="h-3.5 w-3.5" />
                        {fmt(l.asking_price ? Number(l.asking_price) : null)}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold text-foreground">{fmt(l.revenue ? Number(l.revenue) : null)}</td>
                    <td className="px-4 py-3 font-bold text-foreground">{fmt(l.ebitda ? Number(l.ebitda) : null)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {locationStr(l) ? (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {locationStr(l)}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleEdit(e, l.id)}
                            title={l.listing_status_id !== 3 ? "Republish" : "Update"}
                            className={`h-7 w-7 ${l.listing_status_id !== 3 ? "text-emerald-600 hover:bg-emerald-500/10" : "text-primary hover:bg-primary/10"}`}
                          >
                            {l.listing_status_id !== 3 ? <RefreshCw className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                          </Button>
                        )}
                        {canManage && l.listing_status_id === 3 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleShare(e, l.id, l.title)}
                            title="Share with someone outside BusinessLocating"
                            className="h-7 w-7 text-primary hover:bg-primary/10"
                          >
                            <Share2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={(e) => handleDelete(e, l.id, l.title)} className="h-7 w-7 text-destructive hover:bg-destructive/10">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                        <Link to={`/listings/${l.id}`} state={{ from: backTo }}>
                          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-foreground text-background transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                            <ArrowRight className="h-3.5 w-3.5" />
                          </span>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page}
          totalElements={total}
          pageSize={PAGE_SIZE}
          onChange={goToPage}
          className="mt-6"
        />
      </PagePanel>

      {shareTarget && (
        <ShareListingModal
          listingId={shareTarget.id}
          listingTitle={shareTarget.title}
          onClose={() => setShareTarget(null)}
        />
      )}
    </PageShell>
  );
}
